import React, { useState } from "react";
import {Modal, Select, theme, Tooltip} from "antd";
import ColorButton from "../../common/ColorButton";
import { CheckCircleFilled, MinusCircleFilled, ExclamationCircleOutlined, StopFilled } from "@ant-design/icons";
import TSTable from "../../common/TSTable";
import { URL_TANK } from "../../constants/urls";
import { TANK_ACTION, TANK_STATUS, TANK_STATUS_OPTIONS, TANK_STATUS_TH } from "../../constants/string";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDroplet, faFlask, faStore } from "@fortawesome/free-solid-svg-icons";
import { dateToHumanString, formatComma } from "../../frameworks/Util";
import { GRAY_COLOR, PRIMARY_COLOR } from "../../constants/color";
import { POST } from "../../frameworks/HttpClient";
import ErrorMessages from "../../common/ErrorMessages";

export default function StockConcentrateModal(props){
  const { open, onClose } = props;
  const tableRef = React.useRef(null);
  const [errorMessages , setErrorMessages] = useState(null);
  const { token: { colorWarning, colorSuccess }, } = theme.useToken();
  const [filterStatus, setFilterStatus] = React.useState(TANK_STATUS.SELLING)

  const refreshData = async (params) => { 
    if (tableRef.current){
      await tableRef.current.fetch(params);
    }};

  const refreshDataWithoutResetPage = async (params) => { 
    if (tableRef.current){
      await tableRef.current.fetchWithoutResetPage(params);
    }};

  const confirm = (title, value) => {
    Modal.confirm({
      title: `เปิดแทงก์ขาย - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color: PRIMARY_COLOR }} />,
      content: "คุณต้องการเปิดแทงก์เพื่อขายใช่หรือไม่ ?",
      okText: "ใช่",
      cancelText: "ปิด",
      maskClosable: true,
      okButtonProps: { style: { backgroundColor: PRIMARY_COLOR } },
      onOk: async () => {
        await handleOpenTankSell(value);
        refreshDataWithoutResetPage();
      },
      onConfirm: () => refreshDataWithoutResetPage(),
    });
  };

  const confirmEnd = (title, value) => {
    Modal.confirm({
      title: `จบการทำงาน - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color: PRIMARY_COLOR }} />,
      content: "คุณต้องการจบการทำงานล็อตน้ำยางข้นนี้ใช่หรือไม่ ?",
      okText: "ใช่",
      cancelText: "ปิด",
      maskClosable: true,
      okButtonProps: { style: { backgroundColor: PRIMARY_COLOR } },
      onOk: () => {
        handleEndWork(value);
      },
      onConfirm: () => refreshDataWithoutResetPage(),
    });
  };

  const handleOpenTankSell = async (id) => {
    setErrorMessages(null);
    try {
      const data = { action: TANK_ACTION.SELL };
      await POST(`${URL_TANK.TANK_TRANSACTION}${id}/change-state/`, data);
      refreshDataWithoutResetPage();
    } catch (error) {
      setErrorMessages (error.errorMessages);
    }
  };

  const handleEndWork = async (id) => {
    setErrorMessages(null);
    try {
      const data = { action: TANK_ACTION.END };
      await POST(`${URL_TANK.TANK_TRANSACTION}${id}/change-state/`, data);
      refreshDataWithoutResetPage();
    } catch (error) {
      setErrorMessages(error.errorMessages);
    }
  };

  const columns = [
    {
      title: 'Action',
      dataIndex: 'state',
      key: 'action',
      render : (value, original) => <>
        {value === TANK_STATUS.CLOSED && (
          <Tooltip triger='hover' title={"ขาย"}>
            <ColorButton
              type="primary"
              style={{ marginLeft: 10 }}
              override={colorSuccess}
              icon={<FontAwesomeIcon icon={faStore} />}
              onClick={() =>
                confirm(original.lot_no, original.id  )
              }
            />
          </Tooltip>)}
          {original.state === TANK_STATUS.SELLING && (
              <Tooltip triger='hover' title={"จบการทำงาน"}>
                <ColorButton
                  type="primary"
                  override={GRAY_COLOR}
                  style={{ marginLeft: 10 }}
                  icon={<StopFilled />}
                  onClick={() =>
                    confirmEnd(original.lot_no, original.id)
                  }
                />
              </Tooltip>
            )}
      </>
    },
    {
      title: 'วันที่สร้าง',
      dataIndex: 'created',
      key: 'created',
      render :(value) => <center>{dateToHumanString(value)}</center>
    },
    {
      title: 'แทงก์',
      dataIndex: 'tank_name',
      key: 'tank_name',
      searcher: true,
      render : (value) => value ? value : '-'
    },
    {
      title: 'Lot No.',
      dataIndex: 'lot_no',
      key: 'lot_no',
      searcher: true,
      render : (value) => value ? value : '-'
    },
    {
      title: "มีใบแจ้งผลิต",
      dataIndex: "produce_lot_no",
      key: "produce_lot_no",
      render:(value, option) => 
        <center> 
          {value ? 
            <Tooltip title={
              <div>
                Contract No. : {option.sale_order_no}<br/>
                ล็อตใบแจ้งผลิต : {option.produce_lot_no}
              </div>
            }>
              <CheckCircleFilled style={{color : colorSuccess, fontSize : 16}}/> 
            </Tooltip>
          : 
            <Tooltip title={'ยังไม่ได้ผูกกับใบแจ้งผลิต'}>
              <MinusCircleFilled style={{color : colorWarning, fontSize : 16}}/>
            </Tooltip>
          }
        </center> 
    },
    {
      title: 'เกรด',
      dataIndex: 'product_name',
      key: 'product_name',
      render : (value) => value ? value : '-'
    },
    {
      title: 'น้ำหนัก (Kgs)',
      dataIndex: 'current_weight',
      key: 'current_weight',
      render : (value) => <center>{value ? formatComma(value) : '-'}</center>
    },
    {
      title: 'สถานะ',
      dataIndex: 'state', 
      key: 'state',
      render : (value, original) => value ? TANK_STATUS_TH[value] : '-'
    },
  ]

  React.useEffect(() => {
    const params = {state : filterStatus}
    refreshData(params);
  },[filterStatus])

  return(
    <Modal
      title={"สต๊อกน้ำยางข้น"}
      open={open}
      onCancel={onClose}
      okText={"ตกลง"}
      cancelText={"ยกเลิก"}
      width={1000}
      destroyOnClose
      footer={[
        <ColorButton onClick={()=> onClose()}>ปิด</ColorButton>
      ]}
    >
      <ErrorMessages message={errorMessages}/>
      สถานะ : 
      <Select
        options={TANK_STATUS_OPTIONS}
        value={filterStatus}
        onSelect={(value) => setFilterStatus(value)}
        style={{ width : 250, marginBottom : 10, marginLeft : 10 }}
      />
      <TSTable
        ref={tableRef}
        columns={columns}
        size={"small"}
        url={URL_TANK.TANK_TRANSACTION}
        params={{state : filterStatus}}
      />

    </Modal>
    
  )
}